import type { JSX } from 'react';
import { useEffect } from 'react';
import type { Section } from '@common/formEngine/formEngine.types';
import type { Process } from '@common/interfaces';
import type { ExternalReferralContentKeys } from '@common/validation/customControls/externalReferral';
import type { HearingLossAudiologistConsultDataBase } from '@common/validation/customControls/hearingLoss/hearingLossAudiologistConsultDataBase';
import type {
    HearingLossExaminationDataV2,
    RedenVerwijzing,
} from '@common/validation/customControls/hearingLoss/hearingLossExamination/hearingLossExaminationV2';
import type { ContentComponentsMap, InputComponentsMap } from '@frontend/formEngine/editForm/editForm.types';
import { CustomControlCode, ProcessActivityStatus } from '@common/enums';
import { getInputField } from '@common/formEngine/typedSections';
import { getCustomControlData } from '@common/utils/processUtil';
import { hearingLossAudiologistConsultSections } from '@common/validation/customControls/hearingLoss/hearingLossAudiologistConsult/hearingLossAudiologistConsult';
import { redenReceptData } from '@common/validation/customControls/hearingLoss/utils';
import { FormParagraphFormGroup } from '@frontend/components/fields/FormParagraphField';
import { ProcessContext } from '@frontend/components/pages/episodeCategory/carePath/process/processContext';
import { EditForm } from '@frontend/formEngine/editForm/EditForm';
import { useProcessActivityEditContext } from '@frontend/utils';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { FormParagraphFormGroupPrefilled } from '../hearingLossComponents/FormParagraphFormGroupPrefilled';
import { HearingLossAudiologistConsultBijgevoegdeInformatie } from './HearingLossAudiologistConsultBijgevoegdeInformatie';

type HearingLossAudiologistConsultEditProps<Data extends HearingLossAudiologistConsultDataBase> = Readonly<{
    isFollowUp?: boolean;
    sections: Section<Data, ExternalReferralContentKeys>[];
}>;

export function HearingLossAudiologistConsultEdit<Data extends HearingLossAudiologistConsultDataBase>({
    isFollowUp = false,
    sections,
}: HearingLossAudiologistConsultEditProps<Data>): JSX.Element {
    const { data, onChange } = useProcessActivityEditContext<HearingLossAudiologistConsultDataBase>();
    const { process } = useDefinedContext(ProcessContext);

    useEffect(() => {
        const redenVerwijzing = loadRedenVerwijzing(process);
        onChange(redenVerwijzing);
    }, [process]);

    return (
        <EditForm
            contentComponents={getContentComponents(isFollowUp)}
            inputComponents={getInputComponents()}
            sections={sections}
        />
    );

    function getInputComponents(): InputComponentsMap<keyof Data> {
        const map: InputComponentsMap<keyof Data> = new Map();

        map.set(
            'reden_verwijzing',
            <FormParagraphFormGroup label="Reden verwijzing">{data.reden_verwijzing}</FormParagraphFormGroup>
        );
        map.set(
            'reden_reden_verwijzing_anders',
            <FormParagraphFormGroup label="Reden anders">{data.reden_reden_verwijzing_anders}</FormParagraphFormGroup>
        );

        for (const { key, value } of redenReceptData) {
            map.set(
                key,
                <FormParagraphFormGroupPrefilled
                    fieldKey={key}
                    label={getInputField(hearingLossAudiologistConsultSections, key).label}
                    value={value}
                />
            );
        }

        return map;
    }
}

export function getContentComponents(isFollowUp: boolean): ContentComponentsMap<ExternalReferralContentKeys> {
    const map: ContentComponentsMap<ExternalReferralContentKeys> = new Map();
    map.set('bijgevoegde_informatie', <HearingLossAudiologistConsultBijgevoegdeInformatie isFollowUp={isFollowUp} />);

    return map;
}

function loadRedenVerwijzing(process: Process): {
    reden_reden_verwijzing_anders: string | null;
    reden_verwijzing: RedenVerwijzing;
} {
    const customControlData = getCustomControlData<HearingLossExaminationDataV2>(
        process,
        CustomControlCode.HearingLossExamination,
        [ProcessActivityStatus.complete]
    );

    if (!customControlData) {
        throw new Error("Can't find custom control data for HearingLossExamination");
    }

    if (!customControlData.reden_verwijzing) {
        throw new Error("Can't find reden_verwijzing in custom control data");
    }

    return {
        reden_reden_verwijzing_anders: customControlData.reden_verwijzing_anders,
        reden_verwijzing: customControlData.reden_verwijzing,
    };
}
